/** @module A short tone and buzz per scan result, so nobody has to look at the screen. */
import { useQuery } from "@tanstack/react-query";

import { api, problemFrom } from "@/api/client";

let context: AudioContext | null = null;

/** Higher for a match, lower for a question, two low ones for nothing found. */
const TONES: Record<"match" | "review" | "none", number[]> = {
  match: [880],
  review: [660],
  none: [330, 330],
};

function play(frequencies: number[]): void {
  try {
    context ??= new AudioContext();
    const start = context.currentTime;
    frequencies.forEach((frequency, index) => {
      if (!context) return;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = frequency;
      // Soft attack and release: a click would sound like a fault.
      const at = start + index * 0.14;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.12, at + 0.01);
      gain.gain.linearRampToValueAtTime(0, at + 0.09);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.1);
    });
  } catch {
    // No audio (old browser, blocked autoplay): the vibration and the strip still tell.
  }
}

export function signalResult(kind: "match" | "review" | "none"): void {
  play(TONES[kind]);
  if (typeof navigator.vibrate === "function") {
    navigator.vibrate(kind === "none" ? [40, 60, 40] : 40);
  }
}

/** Whether the person wants tone and buzz; on unless switched off in the settings. */
export function useFeedbackEnabled(): boolean {
  const settings = useQuery({
    queryKey: ["me", "settings"],
    queryFn: async () => {
      const { data, response } = await api.GET("/api/v1/me/settings", {});
      if (!data) throw problemFrom(response);
      return data;
    },
    staleTime: 5 * 60_000,
  });
  return settings.data?.ui.scanFeedback !== false;
}
